import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type DeathInput } from '@/services/api';
import { leaderboardKeys } from './useLeaderboard';
import { poolKeys } from './usePools';

/**
 * Query key factory for deaths
 */
export const deathKeys = {
  all: ['deaths'] as const,
  pool: (poolId: string) => [...deathKeys.all, poolId] as const,
};

/**
 * Hook to get death records for a pool
 */
export function useDeaths(poolId: string) {
  return useQuery({
    queryKey: deathKeys.pool(poolId),
    queryFn: async () => {
      const response = await api.deaths.getDeaths(poolId);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !!poolId,
  });
}

/**
 * Hook to record a death verification
 */
export function useRecordDeath(poolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeathInput) => {
      const response = await api.deaths.recordDeath(poolId, input);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deathKeys.pool(poolId) });
      queryClient.invalidateQueries({ queryKey: leaderboardKeys.pool(poolId) });
      queryClient.invalidateQueries({ queryKey: poolKeys.members(poolId) });
    },
  });
}
